import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import { capitalize } from 'lodash-es';
import { ReactNode } from 'react';

import { Order } from '~hooks/useOrders/models';
import formatNumber from '~utils/formatNumber';
import {
  formatMaterialRowLabels,
  getDeliveredByMaterialAndUnit,
  getDeliveredForMaterialRow,
  getOrderMaterialRows,
  orderShowsMaterialList,
} from '~utils/orders';

const minutesToHoursAndMinutesString = (value: number) => {
  const minutesToHours = Math.floor(value / 60);
  const minutesLeft = Math.floor(value % 60);

  const minutesToHoursAsString = minutesToHours > 0 ? `${minutesToHours}hr ` : '';
  const minutesLeftAsString = `${minutesLeft}min`;

  return `${minutesToHoursAsString}${minutesLeftAsString}`;
};

export const makeCycleAvgCellString = (order: Order) => {
  return minutesToHoursAndMinutesString(order.loadCycleAvg);
};

const makeLoadsPickupAvgCellString = (order: Order) => {
  return minutesToHoursAndMinutesString(order.loadsPickupTimeMinutesAvg);
};

const OrderSummaryRow = ({ order }: { order: Order }) => {
  const theme = useTheme();
  const { material, orderSummary } = order;
  const deliveredQuantity =
    orderSummary?.deliveredQuantities.find(
      (deliveredQuantity) => deliveredQuantity.materialName === material?.name,
    ) || orderSummary?.deliveredQuantities[0];

  const deliveredContent = orderShowsMaterialList(order) ? (
    <MaterialRowsList order={order} />
  ) : (
    <SummaryValueText>
      {`${formatNumber(deliveredQuantity?.delivered ?? 0)} / ${formatNumber(deliveredQuantity?.total ?? 0)}`}
    </SummaryValueText>
  );

  return (
    <Box
      sx={{
        borderRadius: theme.brandV2.borderRadius,
        border: `1px solid ${theme.brandV2.colors.treadGray7}`,
        overflow: 'hidden',
      }}
    >
      <Table
        sx={{
          backgroundColor: theme.brandV2.colors.treadGray8,
        }}
      >
        <TableBody>
          <TableRow>
            <OrderSummaryAttribute label={t('live_map.active_trucks')}>
              <SummaryValueText>
                {`${orderSummary?.acceptedJobsCount ?? 0} / ${orderSummary?.jobsCount ?? 0}`}
              </SummaryValueText>
            </OrderSummaryAttribute>
            <OrderSummaryAttribute label={capitalize(`${t('common.delivered')}`)}>
              {deliveredContent}
            </OrderSummaryAttribute>
            <OrderSummaryAttribute label={t('live_map.pickup_avg')}>
              <SummaryValueText>{makeLoadsPickupAvgCellString(order)}</SummaryValueText>
            </OrderSummaryAttribute>
            <OrderSummaryAttribute label={t('live_map.cycle_avg')}>
              <SummaryValueText>{makeCycleAvgCellString(order)}</SummaryValueText>
            </OrderSummaryAttribute>
          </TableRow>
        </TableBody>
      </Table>
    </Box>
  );
};

const SummaryValueText = ({ children }: { children: ReactNode }) => (
  <Typography sx={{ fontSize: '12px', fontWeight: 600 }}>{children}</Typography>
);

const MaterialRowsList = ({ order }: { order: Order }) => {
  const deliveredByMaterialAndUnit = getDeliveredByMaterialAndUnit(order);

  return (
    <Box data-test-id="order-summary-material-rows">
      {getOrderMaterialRows(order).map((row) => {
        const labels = formatMaterialRowLabels(
          getDeliveredForMaterialRow(deliveredByMaterialAndUnit, row),
          row.quantity,
          row.unitOfMeasure,
        );

        return (
          <SummaryValueText key={row.id}>
            {t('live_map.material_delivered_row', {
              name: row.label,
              delivered: labels.delivered,
              ordered: labels.orderedWithUnit,
              interpolation: { escapeValue: false },
            })}
          </SummaryValueText>
        );
      })}
    </Box>
  );
};

interface OrderAttributeProps {
  label: string;
  children: ReactNode;
}

const OrderSummaryAttribute = ({ label, children }: OrderAttributeProps) => {
  const theme = useTheme();

  return (
    <TableCell
      align="center"
      padding="none"
      sx={{
        paddingY: 1,
        borderRight: `1px solid ${theme.brandV2.colors.treadGray7}40`,
        borderBottom: 'none',
        '&:last-child': {
          borderRight: 'none',
        },
      }}
      width={'25%'}
    >
      {children}
      <Typography
        color={theme.brandV2.colors.treadGray4}
        sx={{
          fontSize: '12px',
          fontWeight: 600,
        }}
      >
        {label}
      </Typography>
    </TableCell>
  );
};

export default OrderSummaryRow;
